// #753 — Memory sub-page. Holds the Long-term Memory table that used to
// live mid-page on /admin/users/{id}. Per-row 🗑 delete preserved from
// the original implementation. Wrapped in the shared datagrid container.
//
// #786 — adds the "Recently noticed (tentative)" section below the
// confirmed memory grid. This is where auto_heal-extracted
// `confidence='tentative'` rows surface. Pre-#786 this same data
// was rendered on the family Portal; Bas's design call moved it
// here because surfacing extracted conversation facts to the family
// is surveillance (Story C philosophy correction).
//
// #791 — adds the "Interests (per-category)" section BETWEEN the two
// existing sections. Shows ALL `user_interests_structured` rows
// (intake-sourced AND conversation-sourced — both extract_interests
// and auto_heal-then-promoted) grouped by the 7-category field
// taxonomy, with the topic-weight gauge from `user_activity_preferences`
// joined per entry. Admin-only observability — read-only view that
// completes the "what does the system know about this user" picture
// alongside Memory facts and Recently noticed tentatives.
//
// #828 — Recently noticed narrowed from manual-QA gate to
// observability + prune. The per-row Confirm + Edit affordances
// (and their admin_confirm / admin_correct promotion signals) were
// removed: neither admin nor family can realistically review every
// tentative for every user, so the gate was vestigial. Promotion to
// confirmed now happens exclusively through conversation-driven
// signals (cross_call_corroboration, tentative_probe_engagement,
// probe_dealt_without_rejection). Only Delete remains here, for
// the one case humans are uniquely qualified for: "this extraction
// is just wrong, drop it."
//
// #917 — adds a promotion-progress badge to each Recently noticed
// row so the admin can see whether the row is on its way to
// confirmation. The badge reads `corroboration_count` returned by
// `/api/admin/users/<id>/recently-noticed`; the threshold (v0 = 2)
// mirrors `services/memory_reconciliation.py:CORROBORATION_PROMOTION_THRESHOLD`
// and is pinned by `tests/unit/test_917_corroboration_counter.py`.
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';
import { ENGAGEMENT_LABEL } from './labels';

// v1.6.0 — #943: memory storage unified. Two backend changes drive
//   the user-visible behaviour:
//     1. The legacy memories.extract_and_save LLM extractor no longer
//        emits category='hobby' or category='music'. Both go through
//        user_interests_structured exclusively now. The Long-term
//        Memory section therefore stops showing hobby/music duplicates
//        of facts that also appear in the Interests section.
//     2. A new "Legacy data (read-only)" section at the bottom of
//        the page surfaces the `legacy_memories_archive` table — a
//        snapshot of every memories row touched by migration 0086
//        so admins can diff pre/post migration if a fact is missing
//        from the consolidated view.
//   This section is read-only by design: the archive is an immutable
//   audit surface. Deletes happen against `memories` directly through
//   the Long-term Memory section above.
// v1.5.0 — THREE concurrent changes landed in the admin-bundle PR:
//   #926: Memory page Interests section now shows DISTINCT per-entry
//     gauges for sports / screen / places / genres / other (no more
//     bucket collapse). No code change in this component — the
//     backend `topic_to_card_key` returns per-entry slugs for all 7
//     fields post-#926; this version bump marks the user-facing
//     behaviour change.
//   #927: unify admin vocabulary. The Interests section subline used
//     to read `gauge = topic_weight (0–100, default 50 …)` — four
//     different terms for the same engagement signal lived across
//     the admin pages. Pinned to the canonical `Engagement` label
//     (sourced from `./labels.js`); backend field name `topic_weight`
//     stays unchanged.
// v1.4.1 —
//   #914: Long-term Memory + Interests sections filter to
//     confidence='confirmed' only. Tentative rows live exclusively in
//     Recently noticed below. Section subline counters show tentative
//     volume so admins can see the split at a glance.
//   #917: Recently noticed rows show a promotion-progress badge
//     (`corroborated N/2`) so admins can see which rows are on their
//     way to confirmation under #828's signal-driven promotion model.
const VERSION = 'v1.6.0';

// #917 — promotion-progress threshold mirror.
// Source of truth: `services/memory_reconciliation.py:CORROBORATION_PROMOTION_THRESHOLD`.
// Mirrored here so the admin badge text can render `corroborated N/2`
// without a per-row API call. If the backend threshold changes, this
// constant must change too; the contract test
// `test_917_corroboration_counter.py` pins the backend value.
const CORROBORATION_THRESHOLD = 2;

// #917 — promotion-progress badge for tentative rows.
// Three states:
//   - count >= threshold  → green "eligible for promotion" (the
//     auto-promote already fires on the bump, so this state is rare
//     in practice — covers the case where promotion was attempted
//     and failed for some reason, or where the threshold changed
//     since the row was last bumped).
//   - 0 < count < threshold → amber "needs N more"
//   - count == 0 → grey "awaiting probe or admin confirm"
function corroborationBadge(count) {
  const c = count || 0;
  if (c >= CORROBORATION_THRESHOLD) {
    return {
      text: `corroborated ${c}/${CORROBORATION_THRESHOLD} — eligible for promotion`,
      bg: '#E8F5E9',
      fg: '#1B5E20',
    };
  }
  if (c > 0) {
    const remaining = CORROBORATION_THRESHOLD - c;
    return {
      text: `corroborated ${c}/${CORROBORATION_THRESHOLD} — needs ${remaining} more`,
      bg: '#FFF3E0',
      fg: '#9c6f1a',
    };
  }
  return {
    text: `corroborated 0/${CORROBORATION_THRESHOLD} · awaiting probe or admin confirm`,
    bg: '#EEEEEE',
    fg: '#666',
  };
}

// #791 — locked taxonomy mirror. Source of truth lives in
// `services/structured_interests.py:FIELDS` + `FIELD_LABELS`; we
// mirror here for the admin layout. The pinned test
// `test_admin_user_memory_uses_locked_field_taxonomy` asserts every
// FIELDS entry appears in this list so a future taxonomy change
// can't silently miss this view.
const INTEREST_FIELD_LABELS = [
  ['music_artists',    'Favourite singers / bands'],
  ['music_genres',     'Music styles / genres'],
  ['sports_interests', 'Sports / teams'],
  ['screen_interests', 'Films / TV / actors'],
  ['places_interests', 'Favourite places'],
  ['hobbies',          'Hobbies'],
  ['other_interests',  'Other interests'],
];

// #791 — colour the gauge by weight bucket (matches behaviour ranges
// in services/topic_weights.py).
function gaugeColor(w) {
  if (w >= 70) return '#28A745';       // propose freely (green)
  if (w >= 30) return '#E8845A';       // propose occasionally (coral)
  if (w >= 1)  return '#888';          // don't propose, respond only (grey)
  return '#DC3545';                    // 0 — never propose (red)
}

function sourceBadgeStyle(src) {
  // intake = family-authored at intake (blue)
  // conversation = extract_interests post-call (purple)
  // conversation_auto_heal = auto_heal then promoted (teal)
  const palette = {
    intake:                  { bg: '#E3F2FD', fg: '#1565C0' },
    conversation:            { bg: '#F3E5F5', fg: '#6A1B9A' },
    conversation_auto_heal:  { bg: '#E0F2F1', fg: '#00695C' },
  };
  const c = palette[src] || { bg: '#EEE', fg: '#666' };
  return {
    background: c.bg, color: c.fg,
    padding: '2px 8px', borderRadius: 10,
    fontSize: '0.72rem', fontWeight: 600,
    letterSpacing: '0.02em', whiteSpace: 'nowrap',
  };
}

export default function AdminUserMemory() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [memories, setMemories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // #786 — Recently noticed (tentative) state.
  const [tentative, setTentative] = useState([]);
  const [tentativeError, setTentativeError] = useState(null);
  // #791 — Interests (per-category) state. Grouped dict keyed by field.
  const [interests, setInterests] = useState(null);
  const [interestsError, setInterestsError] = useState(null);
  // #943 — Legacy memories archive state. Read-only snapshot of every
  // `memories` row touched by migration 0086. The archive endpoint is
  // tolerant of the table being absent (returns []) so this loads
  // safely against pre-migration databases.
  const [legacyArchive, setLegacyArchive] = useState([]);
  const [legacyArchiveError, setLegacyArchiveError] = useState(null);

  const familyId = id;

  const refreshTentative = useCallback(async () => {
    try {
      const r = await adminFetch(
        `/api/admin/users/${familyId}/recently-noticed`
      );
      setTentative(Array.isArray(r?.tentative) ? r.tentative : []);
      setTentativeError(null);
    } catch (e) {
      setTentativeError(e.message || 'Failed to load recently noticed');
    }
  }, [familyId]);

  const refreshInterests = useCallback(async () => {
    try {
      const r = await adminFetch(
        `/api/admin/users/${familyId}/structured-interests`
      );
      setInterests(r?.interests || {});
      setInterestsError(null);
    } catch (e) {
      setInterestsError(e.message || 'Failed to load interests');
    }
  }, [familyId]);

  // #943 — load the legacy memories archive. Endpoint is tolerant of
  // the table being absent (returns []) so this never blocks the
  // page on a pre-migration schema.
  const refreshLegacyArchive = useCallback(async () => {
    try {
      const r = await adminFetch(
        `/api/admin/users/${familyId}/legacy-memories-archive`
      );
      setLegacyArchive(Array.isArray(r?.archive) ? r.archive : []);
      setLegacyArchiveError(null);
    } catch (e) {
      setLegacyArchiveError(e.message || 'Failed to load legacy archive');
    }
  }, [familyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await adminApi.getUser(id);
        if (cancelled) return;
        setUser(u);
        // memories live on the elderly_user_id; same resolution
        // pattern as the original Long-term Memory section.
        const memUid = u.elderly_user_id || id;
        const m = await adminFetch(`/api/admin/users/${memUid}/memories`);
        if (cancelled) return;
        setMemories(m?.memories || []);
        // #786 + #791 — load tentative auto_heal rows + structured
        // interests. Both endpoints resolve family→elderly
        // internally, so we hit them with the URL `id`
        // (family_user_id), not memUid.
        await Promise.all([
          refreshTentative(),
          refreshInterests(),
          refreshLegacyArchive(),
        ]);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, refreshTentative, refreshInterests, refreshLegacyArchive]);

  async function deleteMemory(mid) {
    if (!window.confirm('Delete this memory fact?')) return;
    const memUid = user.elderly_user_id || id;
    try {
      await adminFetch(`/api/admin/users/${memUid}/memories/${mid}`, {
        method: 'DELETE',
      });
      setMemories(prev => prev.filter(m => m.id !== mid));
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  }

  // #828 — Delete is the only admin action left on tentative rows.
  // Hits the dedicated DELETE endpoint; no promotion happens here
  // (promotion is fully conversation-driven now).
  async function deleteTentative(row) {
    try {
      await adminFetch(
        `/api/admin/users/${familyId}/recently-noticed`
          + `/${encodeURIComponent(row.target_table)}`
          + `/${encodeURIComponent(row.id)}`,
        { method: 'DELETE' }
      );
      setTentative(prev => prev.filter(r => r.id !== row.id));
    } catch (e) {
      setTentativeError(`Could not delete — ${e.message}`);
    }
  }

  return (
    <AdminLayout title="Memory">
      <style>{userSubPageStyles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · Long-term memory sub-page
          </div>
        </div>
      )}

      {/* #914 — Long-term Memory filtered to confirmed-only.
          Tentative rows live exclusively in the Recently noticed
          section below. Pre-#914 a tentative row appeared in BOTH
          places, making it ambiguous whether the system "knew" a
          fact or was merely watching it. Post-#914: confirmed rows
          are facts Berta references; tentative rows are the
          to-be-corroborated pile in Recently noticed. */}
      {(() => {
        const confirmedMemories = (memories || []).filter(
          m => (m.confidence ?? 'confirmed') === 'confirmed'
        );
        const tentativeMemoriesCount = (memories || []).filter(
          m => m.confidence === 'tentative'
        ).length;
        return (
        <>
      <div className="section-title">
        Long-term Memory
        <span className="section-sub">
          memories table · confirmed facts the companion references.
          {tentativeMemoriesCount > 0 && (
            <> · <strong>{tentativeMemoriesCount} tentative</strong> in Recently noticed below</>
          )}
          {' '}· 🗑 deletes a single row.
        </span>
      </div>
      {memories && confirmedMemories.length === 0 && (memories.length === 0
        ? <div className="empty-state">No memories stored yet.</div>
        : <div className="empty-state">No confirmed memories yet — {memories.length} tentative {memories.length === 1 ? 'row is' : 'rows are'} in Recently noticed below.</div>
      )}
      {confirmedMemories.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Key</th>
                <th>Value</th>
                <th>Source</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {confirmedMemories.map(m => (
                <tr key={m.id}>
                  <td><span style={{ fontWeight: 600, color: '#E8845A' }}>{m.category}</span></td>
                  <td style={{ color: '#888', fontSize: '0.82rem' }}>{m.key}</td>
                  <td>
                    {m.sensitive && (
                      <span className="badge badge-sensitive" style={{ marginRight: 6 }}>private</span>
                    )}
                    {m.value}
                  </td>
                  <td style={{ color: '#aaa', fontSize: '0.82rem' }}>{m.source}</td>
                  <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => deleteMemory(m.id)}
                      style={{
                        background: 'none', border: 'none', color: '#DC3545',
                        cursor: 'pointer', fontSize: '0.85rem',
                        padding: '2px 6px', borderRadius: 4,
                      }}
                      title="Delete memory"
                    >🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
        );
      })()}

      {/* #791 — Interests (per-category). All user_interests_structured
          rows grouped by the 7-category locked taxonomy, with source
          attribution (intake / conversation / conversation_auto_heal)
          + topic-weight gauge per entry. Read-only admin observability;
          editing happens via Long-term Memory above (memories table)
          or Recently noticed below (tentative auto_heal rows). */}
      {/* #914 — Interests filtered to confirmed-only. Tentative
          entries live exclusively in Recently noticed below. Counter
          in the section subline surfaces the tentative volume. */}
      {(() => {
        let totalTentativeInterests = 0;
        for (const [field] of INTEREST_FIELD_LABELS) {
          const fieldEntries = (interests && interests[field]) || [];
          totalTentativeInterests += fieldEntries.filter(
            it => it.confidence === 'tentative'
          ).length;
        }
        return (
      <>
      <div className="section-title" style={{ marginTop: 32 }}>
        Interests (per-category)
        <span className="section-sub">
          user_interests_structured table · confirmed only ·
          {' '}{ENGAGEMENT_LABEL} gauge (0–100, default 50 = no signal yet){totalTentativeInterests > 0 && (
            <> · <strong>{totalTentativeInterests} tentative</strong> in Recently noticed below</>
          )}
        </span>
      </div>
      {interestsError && (
        <p className="error-state">Error: {interestsError}</p>
      )}
      {interests && (
        <div className="data-grid">
          {INTEREST_FIELD_LABELS.map(([field, label]) => {
            const allEntries = interests[field] || [];
            const entries = allEntries.filter(
              it => (it.confidence ?? 'confirmed') === 'confirmed'
            );
            return (
              <div key={field} style={{ marginBottom: 18 }}>
                <div style={{
                  fontWeight: 600, color: '#444', fontSize: '0.9rem',
                  padding: '6px 0', borderBottom: '1px solid #eee',
                  marginBottom: 6,
                }}>
                  {label}
                  <span style={{
                    color: '#999', fontWeight: 400, fontSize: '0.8rem',
                    marginLeft: 8,
                  }}>
                    ({entries.length})
                  </span>
                </div>
                {entries.length === 0 ? (
                  <div className="empty-state" style={{
                    padding: '6px 0', fontSize: '0.82rem', color: '#aaa',
                  }}>
                    (no entries)
                  </div>
                ) : (
                  <table className="data-table" style={{ marginBottom: 0 }}>
                    <tbody>
                      {entries.map((it, i) => {
                        const w = it.topic_weight ?? 50;
                        return (
                          <tr key={`${field}-${i}-${it.entry}`}>
                            <td style={{ minWidth: 180 }}>
                              <strong>{it.entry}</strong>
                            </td>
                            <td style={{ width: 200 }}>
                              {/* Gauge bar: width % = weight, colour by bucket */}
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                              }}>
                                <div style={{
                                  background: '#eee', width: 120, height: 8,
                                  borderRadius: 4, overflow: 'hidden',
                                }}>
                                  <div style={{
                                    background: gaugeColor(w),
                                    width: `${Math.max(0, Math.min(100, w))}%`,
                                    height: '100%',
                                  }} />
                                </div>
                                <span style={{
                                  color: gaugeColor(w), fontWeight: 600,
                                  fontSize: '0.82rem',
                                }}>{w}</span>
                              </div>
                            </td>
                            <td style={{ width: 200 }}>
                              <span style={sourceBadgeStyle(it.source)}>
                                {it.source}
                              </span>
                              {it.confidence === 'tentative' && (
                                <span style={{
                                  ...sourceBadgeStyle('intake'),
                                  background: '#FFF9C4', color: '#7a6a3f',
                                  marginLeft: 6,
                                }}>tentative</span>
                              )}
                            </td>
                            <td style={{
                              color: '#aaa', fontSize: '0.82rem',
                              whiteSpace: 'nowrap',
                            }}>
                              {it.created_at
                                ? new Date(it.created_at).toLocaleDateString()
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      );
      })()}

      {/* #786 → #828 — Recently noticed (tentative). Auto_heal-extracted
          rows that have not yet been promoted to confirmed. #828
          narrowed admin actions to Delete only; promotion is now
          fully conversation-driven (cross_call_corroboration,
          tentative_probe_engagement, probe_dealt_without_rejection). */}
      <div className="section-title" style={{ marginTop: 32 }}>
        Recently noticed (tentative)
        <span className="section-sub">
          auto_heal extractor · facts heard in recent conversations · observability + manual prune (promotion is conversation-driven)
        </span>
      </div>
      {tentativeError && (
        <p className="error-state">Error: {tentativeError}</p>
      )}
      {!tentativeError && tentative.length === 0 && (
        <div className="empty-state">No tentative rows in the panel window.</div>
      )}
      {tentative.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Key</th>
                <th>Value</th>
                <th>Promotion progress</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* #786 → #828 Recently noticed body */}
              {tentative.map(row => {
                // #917 — render promotion-progress badge so the admin
                // can tell at a glance whether a tentative row is on
                // its way to confirmation or stuck.
                const badge = corroborationBadge(row.corroboration_count);
                const lastSeen = row.last_corroborated_at
                  ? new Date(row.last_corroborated_at).toLocaleDateString()
                  : null;
                return (
                  <tr key={row.id}>
                    <td><span style={{ fontWeight: 600, color: '#84B6EB' }}>{row.category}</span></td>
                    <td style={{ color: '#888', fontSize: '0.82rem' }}>{row.key}</td>
                    <td>{row.value}</td>
                    <td>
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          whiteSpace: 'nowrap',
                        }}
                        title={
                          lastSeen
                            ? `Last corroborated on ${lastSeen}`
                            : 'Not yet corroborated in a separate call'
                        }
                      >
                        {badge.text}
                      </span>
                    </td>
                    <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => deleteTentative(row)}
                        style={{
                          background: 'none', border: 'none', color: '#DC3545',
                          cursor: 'pointer', fontSize: '0.85rem',
                          padding: '2px 6px', borderRadius: 4,
                        }}
                        title="Delete tentative row"
                      >🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* #943 — Legacy data (read-only). Snapshot of every memories
          row that migration 0086 touched (the hobby/music unification).
          Read-only audit surface: shows what the row looked like
          BEFORE forward-migration into user_interests_structured, so
          admins can diff if a fact is missing from the consolidated
          view above. The section is hidden when the archive is empty
          to keep the page uncluttered for users who never had
          legacy hobby/music data. */}
      {legacyArchive.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: 32 }}>
            Legacy data (read-only)
            <span className="section-sub">
              legacy_memories_archive table · pre-#943 snapshot · {legacyArchive.length} row{legacyArchive.length === 1 ? '' : 's'} · forward-migrated rows now live in the sections above
            </span>
          </div>
          {legacyArchiveError && (
            <p className="error-state">Error: {legacyArchiveError}</p>
          )}
          <div className="data-grid">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Key</th>
                  <th>Value</th>
                  <th>Source</th>
                  <th>Confidence</th>
                  <th>Archive reason</th>
                  <th>Original date</th>
                </tr>
              </thead>
              <tbody>
                {legacyArchive.map(row => (
                  <tr key={row.id}>
                    <td>
                      <span style={{ fontWeight: 600, color: '#888' }}>
                        {row.category}
                      </span>
                    </td>
                    <td style={{ color: '#888', fontSize: '0.82rem' }}>
                      {row.key}
                    </td>
                    <td>{row.value}</td>
                    <td style={{ color: '#aaa', fontSize: '0.82rem' }}>
                      {row.source}
                    </td>
                    <td style={{ color: '#aaa', fontSize: '0.82rem' }}>
                      {row.confidence}
                    </td>
                    <td style={{ color: '#aaa', fontSize: '0.78rem' }}>
                      {row.archive_reason}
                    </td>
                    <td style={{
                      color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap',
                    }}>
                      {row.original_created_at
                        ? new Date(row.original_created_at).toLocaleDateString()
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
